import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { useRef, useState } from "react";
import { acceptFor, UPLOAD_LIMITS } from "@/constants/uploadLimits";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useR2Upload } from "@/hooks/useR2Upload";

interface CustomVideoUnlockedProps {
  videoUrl: string;
  onChange: (url: string) => void;
}

const VIDEO = UPLOAD_LIMITS.video;

export function CustomVideoUnlocked({ videoUrl, onChange }: CustomVideoUnlockedProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { campaignId } = useCampaignBuilder();
  const { upload, isUploading } = useR2Upload();

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadError(null);

    if (!VIDEO.mimeTypes.includes(file.type)) {
      setUploadError("Unsupported format. Please upload an MP4 or WebM video.");
      return;
    }
    if (file.size > VIDEO.maxBytes) {
      setUploadError(`File must be under ${VIDEO.maxSizeLabel}.`);
      return;
    }

    try {
      // Upload to R2 (like logos/banners) so the video persists — a blob URL would die on save.
      const url = await upload(file, "video", campaignId);
      onChange(url);
    } catch {
      setUploadError("Upload failed. Please try again.");
    }
  }

  function handleRemove() {
    onChange("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <div
      style={{
        border: "1px solid var(--color-good-sage-4)",
        background: "var(--color-good-sage-5)",
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div
        className="flex items-center gap-2 font-lato font-semibold"
        style={{ marginBottom: 12, fontSize: 13, color: "var(--color-good-sage)" }}
      >
        <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />
        Custom Video Unlocked
      </div>

      {videoUrl ? (
        <div
          className="flex items-center gap-2 font-lato"
          style={{ fontSize: 12, color: "var(--color-graphite)" }}
        >
          <VideocamOutlinedIcon sx={{ fontSize: 16, color: "var(--color-good-sage)" }} />
          <span
            style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            Custom video attached
          </span>
          <button
            type="button"
            onClick={handleRemove}
            className="flex items-center justify-center text-smoke hover:text-graphite transition-colors"
            aria-label="Remove custom video"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => !isUploading && fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full flex flex-col items-center gap-1 font-lato transition-colors hover:border-dark-dust hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            border: "2px dashed var(--color-smoke)",
            borderRadius: 8,
            padding: 20,
            textAlign: "center",
            cursor: "pointer",
            background: "transparent",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-smoke)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="16 16 12 12 8 16" />
            <line x1="12" y1="12" x2="12" y2="21" />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
          </svg>
          <p style={{ fontSize: 13, color: "var(--color-graphite)", margin: 0 }}>
            {isUploading ? "Uploading…" : "Click to upload your video"}
          </p>
          <span style={{ fontSize: 11, color: "var(--color-smoke)" }}>
            {`MP4 or WebM — max ${VIDEO.maxSizeLabel}`}
          </span>
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptFor("video")}
        className="sr-only"
        onChange={handleFileChange}
      />
      {uploadError && (
        <p className="font-lato" style={{ fontSize: 12, color: "var(--color-wine)", marginTop: 8 }}>
          {uploadError}
        </p>
      )}
    </div>
  );
}
